/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import stringResource from 'ba-filter-nls';
import { FlexLayout, FlexItem } from '@waca/ca-ui-toolkit';
import ApplyButton from './ApplyButton';
import FilterHeader from './FilterHeader';
import NumericFilterPane from './NumericFilterPane';
import { FILTER_TYPE } from './filterProps';

/*
This is a common filter component that allows a filter to be applied using a
slider control. There are also options to enter to/from filter values directly
via text boxes, invert the current selection and specify whether to include
blank values. The filter type can be to switched to a 'list' filter which allows the user
to select/deselect from a list of values.
*/
export default class CompactNumericFilter extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		title: PropTypes.string.isRequired,
		menuTitle: PropTypes.string,
		onChange: PropTypes.func.isRequired,
		onApply: PropTypes.func.isRequired,
		filterSelectionType: PropTypes.oneOf([FILTER_TYPE.RANGE, FILTER_TYPE.SELECT_VALUE]),
		locale: PropTypes.string,
		includeNulls: PropTypes.bool,
		inverted: PropTypes.bool,
		onClear: PropTypes.func,
		loading: PropTypes.bool,
		readOnly: PropTypes.bool,
		allowIncludeNulls: PropTypes.bool,
		allowSwitch: PropTypes.bool,
		allowSearch: PropTypes.bool,
		fromValue: PropTypes.number,
		toValue: PropTypes.number,
		hasMoreData: PropTypes.bool,
		isDirty: PropTypes.bool,
		isSearching: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		filterSelectionType: FILTER_TYPE.RANGE,
		minimumValue: 0,
		maximumValue: 100,
		fromValue: 0,
		toValue: 0,
		includeNulls: false,
		inverted: false,
		decimalPlaces: 5,
		selectedValuesCount: 0,
		loading: false,
		showLabels: true,
		readOnly: false,
		allowIncludeNulls: true,
		allowSwitch: true,
		allowSearch: true,
		locale: undefined,
		hasMoreData: false,
	};

	state = {
		showSearchInput: false,
		showSearchButton: false, // default is range, which has no search
		isDirty: false,
		// these states are used to determine dirty
		// these values are set in _resetDirty()
		fromValue: undefined,
		toValue: undefined,
		inverted: undefined,
	};

	componentWillMount() {
		this._resetDirty();
	}

	componentWillReceiveProps(nextProps) {
		this.setState({
			isDirty:
				this.state.fromValue !== nextProps.fromValue ||
				this.state.toValue !== nextProps.toValue ||
				this.state.inverted !== nextProps.inverted,
		});
	}

	_resetDirty = () => {
		this.setState({
			isDirty: false,
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			inverted: this.props.inverted,
		});
	};

	_onSearchClick = () => {
		this.setState(prevState => ({ showSearchInput: !prevState.showSearchInput }));
	};

	_onShowMenu = () => {
		// TODO Add Menu
	};

	// Range filter type has changed, can be FILTER_TYPE.RANGE or FILTER_TYPE.SELECT_VALUE
	_onChangeType = () => {
		const { onChange, filterSelectionType, allowSearch } = this.props;
		const newFilterSelectionType =
			filterSelectionType === FILTER_TYPE.RANGE
				? FILTER_TYPE.SELECT_VALUE
				: FILTER_TYPE.RANGE;

		onChange && onChange({ filterSelectionType: newFilterSelectionType });
		if (allowSearch) {
			this.setState({ showSearchButton: newFilterSelectionType !== FILTER_TYPE.RANGE });
		}
	};

	_onToggleIncludeNulls = () => {
		const { onChange } = this.props;

		onChange && onChange({ includeNulls: !this.props.includeNulls });
	};

	_onInvert = () => {
		const { onChange } = this.props;

		onChange &&
			onChange({
				inverted: !this.props.inverted,
				fromValue: this.props.fromValue,
				toValue: this.props.toValue,
			});
	};

	_onClearAll = () => {
		const { onClear } = this.props;

		onClear && onClear();
	};

	_onApply = () => {
		const { onApply } = this.props;

		onApply && onApply();
		this._resetDirty();
	};

	_getContextMenu = () => {
		const menu = [];
		const { filterSelectionType, includeNulls, allowSwitch, allowIncludeNulls } = this.props;

		if (allowSwitch && filterSelectionType === FILTER_TYPE.RANGE) {
			menu.push({ label: stringResource.get('individualItems'), fn: this._onChangeType });
		} else if (allowSwitch) {
			menu.push({ label: stringResource.get('upperRange'), fn: this._onChangeType });
		}

		if (allowIncludeNulls && filterSelectionType === FILTER_TYPE.RANGE) {
			menu.push({
				label: includeNulls
					? stringResource.get('excludeBlankValues')
					: stringResource.get('includeBlankValues'),
				fn: this._onToggleIncludeNulls,
			});
		}
		menu.push({ label: stringResource.get('invert'), fn: this._onInvert });
		menu.push({ label: stringResource.get('clearAll'), fn: this._onClearAll });

		return menu;
	};

	render() {
		const {
			title,
			menuTitle,
			onChange,
			loading,
			readOnly,
			allowSearch,
			locale,
			hasMoreData,
			isSearching,
		} = this.props;
		const { showSearchInput, showSearchButton } = this.state;
		const menu = this._getContextMenu();

		return (
			<FlexLayout fullHeight direction="column" minHeight="212px">
				<FlexItem gutter={[0, 2, 0, 2]}>
					<FilterHeader
						title={title}
						menuTitle={menuTitle}
						showSearch={allowSearch && showSearchButton}
						onSearch={this._onSearchClick}
						onShowMenu={this._onShowMenu}
						menu={menu}
						loading={loading}
						height={40}
						readOnly={readOnly}
					/>
				</FlexItem>
				<FlexItem grow margin={[1, 2, 2, 2]}>
					<NumericFilterPane
						{...this.props}
						allowSearch={allowSearch && showSearchInput}
						onChange={onChange}
						showSelectionType={false}
						showFilterPreferences={false}
						readOnly={readOnly}
						locale={locale}
						isSearching={isSearching}
						hasMoreData={hasMoreData}
					/>
				</FlexItem>
				<FlexItem textAlign="center" gutter={0}>
					<ApplyButton
						dirty={
							this.props.isDirty === true || this.props.isDirty === false
								? this.props.isDirty
								: this.state.isDirty
						}
						onApply={this._onApply}
						loading={loading}
						readOnly={readOnly}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}
}
